/**
 * Builds the Product / ProductGroup JSON-LD for a PDP.
 *
 * This is the PDP's only Product entity: the `aggregateRating` comes from the Bazaarvoice
 * attributes synced to the product, and Bazaarvoice's own rating markup is disabled on the PDP.
 * Google rejects ratings without reviews, so it is omitted when the review count is 0.
 */

import {findLowestPrice} from '@salesforce/retail-react-app/app/utils/product-utils'

const SCHEMA_CONTEXT = 'https://schema.org'
const IN_STOCK = 'https://schema.org/InStock'
const OUT_OF_STOCK = 'https://schema.org/OutOfStock'
const NEW_CONDITION = 'https://schema.org/NewCondition'
// Only these properties are accepted by Google in ProductGroup.variesBy.
const SUPPORTED_VARIES_BY = [
    'color',
    'size',
    'material',
    'pattern',
    'suggestedAge',
    'suggestedGender'
]

const stripQuery = (url) => url.split('?')[0]

const toHttps = (url) => url.replace(/^http:\/\//i, 'https://')

const toImageUrls = (images) => {
    const list = Array.isArray(images) ? images : images?.imageURLs
    return (list || [])
        .map((image) => image?.url)
        .filter((url) => typeof url === 'string' && url.startsWith('http'))
        .map(stripQuery)
        .map(toHttps)
}

const getImages = (product, color) => {
    const pdpImages = product?.c_cloudinary?.pdpImages || []
    const cloudinaryGroup =
        (color && pdpImages.find((group) => group?.color === color)) || pdpImages[0]
    const cloudinaryUrls = toImageUrls(cloudinaryGroup?.images)
    if (cloudinaryUrls.length) return cloudinaryUrls

    const imageGroups = product?.imageGroups || []
    const imageGroup =
        (color &&
            imageGroups.find(({variationAttributes = []}) =>
                variationAttributes.some(
                    ({id, values = []}) => id === 'color' && values[0]?.value === color
                )
            )) ||
        imageGroups[0]
    return (imageGroup?.images || [])
        .map((image) => image?.disBaseLink || image?.link)
        .filter(Boolean)
        .map(stripQuery)
        .map(toHttps)
}

const getVariationValueName = (product, attributeId, value) =>
    product?.variationAttributes
        ?.find(({id}) => id === attributeId)
        ?.values?.find((attributeValue) => attributeValue.value === value)?.name || value

// Same price the PDP displays: pricebook price, or the lowest promotional price when lower.
const getDisplayedPrice = (item) => findLowestPrice({...item, type: undefined})?.minPrice

const getGtin = (item) => item?.upc || item?.ean

const buildOffer = ({url, price, currency, isAvailable}) => {
    if (typeof price !== 'number' || price <= 0 || !currency) return undefined
    return {
        '@type': 'Offer',
        url,
        priceCurrency: currency,
        price: price.toFixed(2),
        availability: isAvailable ? IN_STOCK : OUT_OF_STOCK,
        itemCondition: NEW_CONDITION
    }
}

const withoutEmpty = (object) =>
    Object.fromEntries(
        Object.entries(object).filter(
            ([, value]) =>
                value !== undefined &&
                value !== null &&
                value !== '' &&
                !(Array.isArray(value) && value.length === 0)
        )
    )

const getBrand = (product) => (product?.brand ? {'@type': 'Brand', name: product.brand} : undefined)

const getAggregateRating = (product) => {
    const reviewCount = Number(product?.c_bvReviewCount)
    const ratingValue = Number(product?.c_bvAverageRating)
    if (!(reviewCount > 0) || !(ratingValue > 0)) return undefined
    return withoutEmpty({
        '@type': 'AggregateRating',
        ratingValue,
        reviewCount,
        bestRating: Number(product.c_bvRatingRange) || undefined
    })
}

/**
 * @param {Object} product - SCAPI product (master, variant or standard) as fetched by the PDP.
 * @param {string} canonicalUrl - Absolute PDP URL without query string, same as the canonical link.
 * @returns {Object|null}
 */
export const buildProductSchema = (product, canonicalUrl) => {
    if (!product?.name || !canonicalUrl) return null

    const masterId = product.master?.masterId || product.id
    const currency = product.currency
    const brand = getBrand(product)
    const variants = product.variants || []

    if (!variants.length) {
        return withoutEmpty({
            '@context': SCHEMA_CONTEXT,
            '@type': 'Product',
            '@id': canonicalUrl,
            name: product.name,
            image: getImages(product),
            sku: product.id,
            mpn: masterId,
            gtin: getGtin(product),
            brand,
            aggregateRating: getAggregateRating(product),
            offers: buildOffer({
                url: canonicalUrl,
                price: getDisplayedPrice(product),
                currency,
                isAvailable: product.inventory?.orderable
            })
        })
    }

    const variesBy = (product.variationAttributes || [])
        .filter(({id}) => SUPPORTED_VARIES_BY.includes(id))
        .map(({id}) => `${SCHEMA_CONTEXT}/${id}`)

    const hasVariant = variants.map((variant) => {
        const variationValues = variant.variationValues || {}
        const offerUrl = `${canonicalUrl}?${new URLSearchParams(variationValues).toString()}`
        // When the PDP has loaded this exact variant, its full record carries promotions and UPC.
        const variantDetails = variant.productId === product.id ? product : variant
        return withoutEmpty({
            '@type': 'Product',
            name: product.name,
            image: getImages(product, variationValues.color),
            sku: variant.productId,
            mpn: masterId,
            gtin: getGtin(variantDetails),
            color: variationValues.color
                ? getVariationValueName(product, 'color', variationValues.color)
                : undefined,
            size: variationValues.size
                ? getVariationValueName(product, 'size', variationValues.size)
                : undefined,
            offers: buildOffer({
                url: offerUrl,
                price: getDisplayedPrice(variantDetails),
                currency,
                isAvailable: variant.orderable
            })
        })
    })

    return withoutEmpty({
        '@context': SCHEMA_CONTEXT,
        '@type': 'ProductGroup',
        '@id': canonicalUrl,
        name: product.name,
        image: getImages(product),
        brand,
        aggregateRating: getAggregateRating(product),
        productGroupID: masterId,
        variesBy,
        hasVariant
    })
}

/**
 * Serializes JSON-LD so it is safe to inline inside a <script> tag.
 */
export const serializeSchema = (schema) => JSON.stringify(schema).replace(/</g, '\\u003c')
